import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';

import {
  BeneficiaryCountry,
  CauseTrackingConfig,
  CauseTrackingConfigRegion,
  DonationTrackingService,
} from '../../services/DonationTrackingService';

const COUNTRY_OPTIONS: BeneficiaryCountry[] = ['EGYPT', 'GAZA'];

const CauseTrackingConfigPage: React.FC = () => {
  const { causeId } = useParams<{ causeId: string }>();
  const [config, setConfig] = useState<CauseTrackingConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [country, setCountry] = useState<BeneficiaryCountry>('EGYPT');
  const [mealPrice, setMealPrice] = useState('50');
  const [etaMin, setEtaMin] = useState('3');
  const [etaMax, setEtaMax] = useState('7');
  const [delayReceived, setDelayReceived] = useState('60');
  const [delayProcessing, setDelayProcessing] = useState('86400');
  const [delayDispatching, setDelayDispatching] = useState('172800');
  const [regions, setRegions] = useState<CauseTrackingConfigRegion[]>([]);
  const [newRegion, setNewRegion] = useState('');
  const [newLat, setNewLat] = useState('');
  const [newLng, setNewLng] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [templates, setTemplates] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!causeId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await DonationTrackingService.getCauseConfig(causeId);
      setConfig(data ?? null);
      if (data) {
        setCountry(data.beneficiaryCountry);
        setMealPrice(String(data.mealPrice));
        setEtaMin(String(data.estimatedDeliveryDaysMin));
        setEtaMax(String(data.estimatedDeliveryDaysMax));
        setDelayReceived(String(data.receivedToProcessingDelay));
        setDelayProcessing(String(data.processingToDispatchingDelay));
        setDelayDispatching(String(data.dispatchingToDeliveredDelay));
        setRegions(data.allowedRegions ?? []);
        setIsActive(data.isActive);
        setTemplates(data.notificationTemplates ?? {});
      }
    } catch {
      setError('Failed to load tracking config.');
    } finally {
      setLoading(false);
    }
  }, [causeId]);

  useEffect(() => {
    load();
  }, [load]);

  const addRegion = () => {
    const region = newRegion.trim();
    const lat = Number(newLat);
    const lng = Number(newLng);
    if (!region || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      setError('Region needs a name plus valid latitude and longitude.');
      return;
    }
    setRegions((prev) => [...prev, { region, latitude: lat, longitude: lng }]);
    setNewRegion('');
    setNewLat('');
    setNewLng('');
    setError(null);
  };

  const removeRegion = (index: number) => {
    setRegions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!causeId) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await DonationTrackingService.upsertCauseConfig(causeId, {
        beneficiaryCountry: country,
        mealPrice: Number(mealPrice),
        estimatedDeliveryDaysMin: Number(etaMin),
        estimatedDeliveryDaysMax: Number(etaMax),
        receivedToProcessingDelay: Number(delayReceived),
        processingToDispatchingDelay: Number(delayProcessing),
        dispatchingToDeliveredDelay: Number(delayDispatching),
        allowedRegions: regions,
        notificationTemplates: templates,
        isActive,
      });
      setConfig(saved);
      setNotice('Tracking config saved.');
    } catch {
      setError('Failed to save tracking config.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Button component={RouterLink} to={`/causes/${causeId}`} size="small">
          ← Back to cause
        </Button>
        <Typography variant="h5">Cause Tracking Config</Typography>
        {config && (
          <Chip
            size="small"
            color={config.isActive ? 'success' : 'default'}
            label={config.isActive ? 'Active' : 'Inactive'}
          />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Controls beneficiary country, meal price, status delays, and the
        approximate regions used for the tracking map.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Beneficiary country</InputLabel>
              <Select
                label="Beneficiary country"
                value={country}
                onChange={(e: SelectChangeEvent) =>
                  setCountry(e.target.value as BeneficiaryCountry)
                }
              >
                {COUNTRY_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              label="Meal price"
              type="number"
              value={mealPrice}
              onChange={(e) => setMealPrice(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              size="small"
              fullWidth
              label="ETA min (days)"
              type="number"
              value={etaMin}
              onChange={(e) => setEtaMin(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              size="small"
              fullWidth
              label="ETA max (days)"
              type="number"
              value={etaMax}
              onChange={(e) => setEtaMax(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              label="Received → Processing (s)"
              type="number"
              value={delayReceived}
              onChange={(e) => setDelayReceived(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              label="Processing → Dispatching (s)"
              type="number"
              value={delayProcessing}
              onChange={(e) => setDelayProcessing(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              label="Dispatching → Delivered (s)"
              type="number"
              value={delayDispatching}
              onChange={(e) => setDelayDispatching(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">Active</Typography>
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Allowed routing regions
        </Typography>
        <Stack spacing={1} sx={{ mb: 2 }}>
          {regions.map((region, index) => (
            <Stack
              key={`${region.region}-${index}`}
              direction="row"
              alignItems="center"
              spacing={2}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                {region.region} — {region.latitude.toFixed(4)},{' '}
                {region.longitude.toFixed(4)}
              </Typography>
              <Button size="small" color="error" onClick={() => removeRegion(index)}>
                Remove
              </Button>
            </Stack>
          ))}
          {regions.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Using country defaults — add explicit regions to override.
            </Typography>
          )}
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              label="Region name"
              value={newRegion}
              onChange={(e) => setNewRegion(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              size="small"
              fullWidth
              label="Latitude"
              type="number"
              value={newLat}
              onChange={(e) => setNewLat(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              size="small"
              fullWidth
              label="Longitude"
              type="number"
              value={newLng}
              onChange={(e) => setNewLng(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={addRegion}>
              Add region
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Notification templates (optional)
        </Typography>
        <Grid container spacing={2}>
          {(['RECEIVED', 'PROCESSING', 'DISPATCHING', 'DELIVERED'] as const).map(
            (status) => (
              <Grid item xs={12} md={6} key={status}>
                <TextField
                  size="small"
                  fullWidth
                  label={status}
                  value={templates[status] ?? ''}
                  onChange={(e) =>
                    setTemplates((prev) => ({ ...prev, [status]: e.target.value }))
                  }
                />
              </Grid>
            ),
          )}
        </Grid>
      </Paper>

      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          Save config
        </Button>
        <Button onClick={load} disabled={saving}>
          Reset
        </Button>
      </Stack>
    </Box>
  );
};

export default CauseTrackingConfigPage;
